import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { Waves } from "lucide-react";
import api from "@/lib/api";
import { CATEGORIES, CATEGORY_HEX, STALE_HEX, INFRA_HEX, SEA_REGIONS, SEA_REGION_CENTROIDS, CABLE_HEX, distancePointToLineKm } from "@/lib/maritime";
import TopBar from "@/components/TopBar";
import LeftPanel from "@/components/LeftPanel";
import VesselPanel from "@/components/VesselPanel";
import InfraPanel from "@/components/InfraPanel";
import SlickPanel from "@/components/SlickPanel";
import CablePanel from "@/components/CablePanel";
import PilotPanel from "@/components/PilotPanel";
import PlaybackBar from "@/components/PlaybackBar";
import TrackLegend from "@/components/TrackLegend";

const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas";
const STYLE = {
  version: 8,
  sources: {
    basemap: {
      type: "raster",
      tiles: [`${ESRI}/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`],
      tileSize: 256,
      attribution: "",
    },
    labels: {
      type: "raster",
      tiles: [`${ESRI}/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}`],
      tileSize: 256,
      attribution: "",
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#EAEEF3" } },
    { id: "basemap", type: "raster", source: "basemap" },
    { id: "labels", type: "raster", source: "labels", paint: { "raster-opacity": 0.85 } },
  ],
};
const CENTER = [108.75, 13.25];

export default function MapConsole() {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const mapReady = useRef(false);
  const scrubTimer = useRef(null);
  const playTimer = useRef(null);
  const setupPoll = useRef(null);
  const dataRef = useRef({ list: [], visible: true });
  const loadSlicksRef = useRef(() => {});
  const slickMoveTimer = useRef(null);
  const slickStateRef = useRef({ mode: "live", t: Date.now() });
  const regionMarkersAdded = useRef(false);
  const cablesRef = useRef(null);
  const vesselsRef = useRef([]);
  const selectCableRef = useRef(() => {});
  const selectedCableIdRef = useRef(null);
  const hoverCableIdRef = useRef(null);

  const [vessels, setVessels] = useState([]);
  const [health, setHealth] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedInfra, setSelectedInfra] = useState(null);
  const [selectedSlick, setSelectedSlick] = useState(null);
  const [selectedCable, setSelectedCable] = useState(null);
  const [cableVessels, setCableVessels] = useState([]);
  const [cableHover, setCableHover] = useState(null);
  const [cablesData, setCablesData] = useState(null);
  const [showPilot, setShowPilot] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [trackInfo, setTrackInfo] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [filters, setFilters] = useState({
    categories: new Set(),
    movingOnly: false,
    showStale: true,
  });
  const [layers, setLayers] = useState({ vessels: true, tracks: true, infra: true, slicks: true, cables: false });

  const [showTrack, setShowTrack] = useState(false);
  const [trackWindow, setTrackWindow] = useState({ key: "24h", hours: 24 });

  const [range, setRange] = useState({ start: null, end: null });
  const [mode, setMode] = useState("live"); // live | playback
  const [t, setT] = useState(Date.now());
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(10);

  // ---- filtering ----
  const filtered = useMemo(() => {
    return vessels.filter((v) => {
      if (v.lat == null || v.lon == null) return false;
      if (filters.categories.size && !filters.categories.has(v.category)) return false;
      if (filters.movingOnly && !(v.sog > 0.5)) return false;
      if (!filters.showStale && v.stale) return false;
      return true;
    });
  }, [vessels, filters]);

  const counts = useMemo(() => {
    const c = {};
    filtered.forEach((v) => (c[v.category] = (c[v.category] || 0) + 1));
    return c;
  }, [filtered]);

  // ---- map init ----
  useEffect(() => {
    if (mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      center: CENTER,
      zoom: 4.4,
      attributionControl: false,
    });
    mapRef.current = map;
    if (typeof window !== "undefined") window.__slicktraceMap = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");

    const setup = () => {
      if (!map.isStyleLoaded()) return;
      const addSrc = (id) => {
        if (!map.getSource(id)) map.addSource(id, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      };
      const ensure = (def, onAdd) => {
        if (map.getLayer(def.id)) return;
        try {
          map.addLayer(def);
          if (onAdd) onAdd();
        } catch (e) {
          console.warn("layer add failed", def.id, e?.message);
        }
      };
      const hover = (id) => {
        map.on("mouseenter", id, () => (map.getCanvas().style.cursor = "pointer"));
        map.on("mouseleave", id, () => (map.getCanvas().style.cursor = ""));
      };

      addSrc("vessels");
      addSrc("vessel-vectors");
      addSrc("track");
      addSrc("slicks");
      addSrc("infra");

      // South China Sea disputed island groups (static reference overlay,
      // drawn first so it sits beneath vessels/rigs/slicks). Line + fill only.
      if (!map.getSource("regions")) map.addSource("regions", { type: "geojson", data: SEA_REGIONS });
      ensure({
        id: "regions-fill", type: "fill", source: "regions",
        paint: { "fill-color": "#B45309", "fill-opacity": 0.05 },
      });
      ensure({
        id: "regions-outline", type: "line", source: "regions",
        paint: {
          "line-color": "#B45309", "line-opacity": 0.65,
          "line-width": 1.4, "line-dasharray": [3, 2],
        },
      });
      if (!regionMarkersAdded.current) {
        Object.entries(SEA_REGION_CENTROIDS).forEach(([name, c]) => {
          const el = document.createElement("div");
          el.className = "st-region-label";
          el.setAttribute("data-testid", `region-label-${name.split(" ")[0].toLowerCase()}`);
          el.textContent = name;
          new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(c).addTo(map);
        });
        regionMarkersAdded.current = true;
      }

      // Major undersea cables (default OFF). Line only (placement-free), single
      // violet colour, thin; hover/selected states via feature-state.
      if (!map.getSource("cables")) {
        map.addSource("cables", { type: "geojson", data: cablesRef.current || { type: "FeatureCollection", features: [] }, promoteId: "short_name" });
      } else if (cablesRef.current) {
        map.getSource("cables").setData(cablesRef.current);
      }
      if (!map.getSource("cable-vessels")) {
        map.addSource("cable-vessels", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      }
      if (!map.getSource("pilot-envelope")) {
        map.addSource("pilot-envelope", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      }
      if (!map.getSource("pilot-obs")) {
        map.addSource("pilot-obs", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      }
      ensure({
        id: "pilot-envelope-fill", type: "fill", source: "pilot-envelope",
        paint: { "fill-color": "#22d3ee", "fill-opacity": 0.07 },
      });
      ensure({
        id: "pilot-envelope-line", type: "line", source: "pilot-envelope",
        paint: { "line-color": "#22d3ee", "line-width": 1.2, "line-opacity": 0.7, "line-dasharray": [2, 2] },
      });
      ensure({
        id: "pilot-obs-line", type: "line", source: "pilot-obs",
        paint: {
          "line-color": ["match", ["get", "kind"], "t0", "#F59E0B", "latest", "#34d399", "#94a3b8"],
          "line-width": 2.4, "line-opacity": 0.95,
        },
      });
      ensure({
        id: "pilot-obs-fill", type: "fill", source: "pilot-obs",
        paint: {
          "fill-color": ["match", ["get", "kind"], "t0", "#F59E0B", "latest", "#34d399", "#94a3b8"],
          "fill-opacity": 0.12,
        },
      });
      ensure({
        id: "cables-line", type: "line", source: "cables",
        layout: { visibility: "none", "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": CABLE_HEX,
          "line-dasharray": [2, 2],
          "line-width": ["case",
            ["boolean", ["feature-state", "selected"], false], 3.6,
            ["case", ["boolean", ["feature-state", "hover"], false], 2.6, 1.4]],
          "line-opacity": ["case", ["boolean", ["feature-state", "selected"], false], 1, 0.82],
        },
      }, () => {
        map.on("mousemove", "cables-line", (e) => {
          const f = e.features?.[0];
          if (!f) return;
          const id = f.properties.short_name;
          if (hoverCableIdRef.current && hoverCableIdRef.current !== id) {
            map.setFeatureState({ source: "cables", id: hoverCableIdRef.current }, { hover: false });
          }
          map.setFeatureState({ source: "cables", id }, { hover: true });
          hoverCableIdRef.current = id;
          map.getCanvas().style.cursor = "pointer";
          setCableHover({ name: f.properties.name, short: id, x: e.point.x, y: e.point.y });
        });
        map.on("mouseleave", "cables-line", () => {
          if (hoverCableIdRef.current) {
            map.setFeatureState({ source: "cables", id: hoverCableIdRef.current }, { hover: false });
            hoverCableIdRef.current = null;
          }
          map.getCanvas().style.cursor = "";
          setCableHover(null);
        });
        map.on("click", "cables-line", (e) => {
          const f = e.features?.[0];
          if (f) selectCableRef.current(f.properties.short_name);
        });
      });

      // Heading/course vectors — drawn beneath the dots (line layer = placement-free, stable).
      ensure({
        id: "vessel-vectors-line", type: "line", source: "vessel-vectors",
        layout: { "line-cap": "round" },
        paint: {
          "line-color": ["get", "color"],
          "line-width": ["interpolate", ["linear"], ["zoom"], 4, 1.2, 10, 2.2],
          "line-opacity": 0.9,
        },
      });

      // Vessels dots — the primary data must always render.
      ensure({
        id: "vessels-dot", type: "circle", source: "vessels",
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 4, 6, 6, 12, 9],
          "circle-color": ["get", "color"],
          "circle-stroke-color": "rgba(255,255,255,0.85)",
          "circle-stroke-width": 1.2,
          "circle-opacity": 0.98,
        },
      }, () => {
        map.on("click", "vessels-dot", (e) => {
          const f = e.features?.[0];
          if (f) selectVessel(Number(f.properties.mmsi));
        });
        hover("vessels-dot");
      });

      mapReady.current = true;
      refreshVesselSource(dataRef.current.list, dataRef.current.visible);

      // Supporting layers.
      ensure({
        id: "slicks-fill", type: "fill", source: "slicks",
        paint: {
          "fill-color": ["match", ["get", "cat"],
            "vessel", "#FF2A6D", "infrastructure", "#F59E0B",
            "natural", "#14B8A6", "anthropogenic", "#A855F7", "#FF2A6D"],
          "fill-opacity": 0.32,
        },
      }, () => {
        map.on("click", "slicks-fill", (e) => {
          const f = e.features?.[0];
          if (f) selectSlick(String(f.properties.cerulean_id), f.properties.image_ts);
        });
        hover("slicks-fill");
      });
      ensure({
        id: "slicks-outline", type: "line", source: "slicks",
        paint: {
          "line-color": ["match", ["get", "cat"],
            "vessel", "#FF2A6D", "infrastructure", "#F59E0B",
            "natural", "#14B8A6", "anthropogenic", "#A855F7", "#FF2A6D"],
          "line-width": 1.6, "line-opacity": 0.95,
        },
      });
      ensure({
        id: "track-line", type: "line", source: "track",
        paint: { "line-color": "#1D4ED8", "line-width": 2.6, "line-opacity": 0.9 },
      });
      ensure({
        id: "vessel-selected", type: "circle", source: "vessels",
        filter: ["==", ["get", "mmsi"], -1],
        paint: {
          "circle-radius": 13, "circle-color": "rgba(29,78,216,0.15)",
          "circle-stroke-color": "#1D4ED8", "circle-stroke-width": 2,
        },
      });
      ensure({
        id: "infra-glow", type: "circle", source: "infra",
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 8, 10, 15],
          "circle-color": "rgba(180,83,9,0.10)",
          "circle-stroke-color": ["match", ["get", "label"], "oil", INFRA_HEX.oil, "wind", INFRA_HEX.wind, INFRA_HEX.unknown],
          "circle-stroke-width": 1.2, "circle-stroke-opacity": 0.6,
        },
      });
      ensure({
        id: "infra-dot", type: "circle", source: "infra",
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 4.5, 10, 8],
          "circle-color": ["match", ["get", "label"], "oil", INFRA_HEX.oil, "wind", INFRA_HEX.wind, INFRA_HEX.unknown],
          "circle-stroke-color": "#ffffff", "circle-stroke-width": 1.6,
        },
      }, () => {
        map.on("click", "infra-dot", (e) => {
          const f = e.features?.[0];
          if (f) selectInfra(String(f.properties.gfw_id));
        });
        hover("infra-dot");
      });

      // Highlight ring for vessels near a selected cable (above vessel dots).
      ensure({
        id: "cable-vessels-ring", type: "circle", source: "cable-vessels",
        layout: { visibility: "none" },
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 7, 12, 12],
          "circle-color": "rgba(99,102,241,0.12)",
          "circle-stroke-color": CABLE_HEX, "circle-stroke-width": 2.2,
        },
      });

      loadInfra();
      loadSlicksRef.current();

      // Refresh Cerulean slicks whenever the visible map area changes (debounced).
      map.on("moveend", () => {
        clearTimeout(slickMoveTimer.current);
        slickMoveTimer.current = setTimeout(() => loadSlicksRef.current(), 500);
      });
    };

    map.on("load", setup);
    map.on("styledata", setup);
    setupPoll.current = setInterval(() => {
      if (mapReady.current) clearInterval(setupPoll.current);
      else setup();
    }, 300);

    map.on("error", (e) => console.warn("map error", e?.error?.message));

    return () => {
      clearInterval(setupPoll.current);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- vessel source updates ----
  const refreshVesselSource = useCallback((list, visible) => {
    const map = mapRef.current;
    if (!map || !map.getSource("vessels")) return;
    const features = list.map((v) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [v.lon, v.lat] },
      properties: {
        mmsi: v.mmsi,
        color: v.stale ? STALE_HEX : CATEGORY_HEX[v.category] || STALE_HEX,
      },
    }));
    map.getSource("vessels").setData({ type: "FeatureCollection", features });

    // Heading/course vectors (short lines ~10 min of travel) for moving vessels.
    // Line layer only — never uses MapLibre symbol placement, so it stays stable.
    const vectors = [];
    for (const v of list) {
      if (v.stale || !(v.sog > 0.5)) continue;
      const head = v.heading ?? v.cog;
      if (head === null || head === undefined) continue;
      const distNm = Math.min(v.sog * (10 / 60), 6); // cap length
      const rad = (head * Math.PI) / 180;
      const dLat = (distNm * Math.cos(rad)) / 60;
      const dLon = (distNm * Math.sin(rad)) / (60 * Math.max(0.2, Math.cos((v.lat * Math.PI) / 180)));
      vectors.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [[v.lon, v.lat], [v.lon + dLon, v.lat + dLat]] },
        properties: { color: CATEGORY_HEX[v.category] || STALE_HEX },
      });
    }
    if (map.getSource("vessel-vectors")) {
      map.getSource("vessel-vectors").setData({ type: "FeatureCollection", features: vectors });
    }

    const vis = visible ? "visible" : "none";
    ["vessels-dot", "vessel-vectors-line"].forEach((id) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
    });
  }, []);

  useEffect(() => {
    dataRef.current = { list: filtered, visible: layers.vessels };
    refreshVesselSource(filtered, layers.vessels);
  }, [filtered, layers.vessels, refreshVesselSource]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && mapReady.current && map.getLayer("track-line")) {
      map.setLayoutProperty("track-line", "visibility", layers.tracks ? "visible" : "none");
    }
  }, [layers.tracks]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && mapReady.current && map.getLayer("vessel-selected")) {
      map.setFilter("vessel-selected", ["==", ["get", "mmsi"], selected ?? -1]);
    }
  }, [selected, filtered]);

  // ---- data loading ----
  const loadLive = useCallback(async () => {
    try {
      const d = await api.live();
      setVessels(d.vessels);
    } catch {
      /* ignore transient */
    }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const h = await api.health();
      setHealth(h);
      setRange({ start: h.history?.start, end: h.history?.end });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadHealth();
    const h = setInterval(loadHealth, 10000);
    return () => clearInterval(h);
  }, [loadHealth]);

  useEffect(() => {
    if (mode !== "live") return;
    loadLive();
    const i = setInterval(loadLive, 10000);
    return () => clearInterval(i);
  }, [mode, loadLive]);

  // ---- snapshot (playback) ----
  const loadSnapshot = useCallback(async (ts) => {
    try {
      const d = await api.snapshot(new Date(ts).toISOString());
      setVessels(d.vessels);
    } catch {
      /* ignore */
    }
  }, []);

  const scrub = (ms) => {
    setMode("playback");
    setT(ms);
    clearTimeout(scrubTimer.current);
    scrubTimer.current = setTimeout(() => loadSnapshot(ms), 250);
  };

  const jumpLive = () => {
    setPlaying(false);
    setMode("live");
    loadLive();
  };

  const togglePlay = () => {
    if (mode === "live") {
      const start = range.start ? new Date(range.start).getTime() : Date.now() - 3600_000;
      setMode("playback");
      setT(start);
      loadSnapshot(start);
    }
    setPlaying((p) => !p);
  };

  useEffect(() => {
    clearInterval(playTimer.current);
    if (!playing || mode !== "playback") return;
    playTimer.current = setInterval(() => {
      setT((prev) => {
        const end = range.end ? new Date(range.end).getTime() : Date.now();
        const next = prev + speed * 1000;
        if (next >= end) {
          setPlaying(false);
          loadSnapshot(end);
          return end;
        }
        loadSnapshot(next);
        return next;
      });
    }, 1000);
    return () => clearInterval(playTimer.current);
  }, [playing, mode, speed, range.end, loadSnapshot]);

  // ---- selection & track ----
  const selectVessel = useCallback(
    (mmsi) => {
      setSelectedInfra(null);
      setSelectedSlick(null);
      setSelected(mmsi);
      setShowTrack(true);
      const v = vessels.find((x) => x.mmsi === mmsi);
      // Pick a track window that actually contains this vessel's history so a
      // track always shows on selection (vessels stale >24h have no 24h points).
      const ageH = v?.ts ? (Date.now() - new Date(v.ts).getTime()) / 3600000 : 0;
      const win = ageH <= 24 ? { key: "24h", hours: 24 }
        : ageH <= 168 ? { key: "7d", hours: 168 }
        : { key: "30d", hours: 720 };
      setTrackWindow(win);
      if (v && v.lon != null && mapRef.current) {
        mapRef.current.flyTo({ center: [v.lon, v.lat], zoom: Math.max(mapRef.current.getZoom(), 8), duration: 900 });
      }
    },
    [vessels]
  );

  const selectInfra = useCallback((gfwId) => {
    setSelected(null);
    setSelectedSlick(null);
    setSelectedInfra(gfwId);
  }, []);

  const selectSlick = useCallback((id, imageTs) => {
    setSelected(null);
    setSelectedInfra(null);
    setSelectedSlick(id);
    if (imageTs) {
      const ms = new Date(imageTs).getTime();
      setMode("playback");
      setT(ms);
      loadSnapshot(ms);
    }
  }, [loadSnapshot]);

  const showIncidentOnMap = useCallback((inc) => {
    const map = mapRef.current;
    if (!map || !map.getSource("pilot-obs")) return;
    const obs = inc.observations || [];
    const lastIdx = obs.length - 1;
    const obsFeats = obs.filter((o) => o.geometry).map((o, i) => ({
      type: "Feature",
      properties: { kind: i === 0 ? "t0" : i === lastIdx ? "latest" : "mid", label: o.label },
      geometry: o.geometry,
    }));
    const fc = inc.forecasts?.[inc.forecasts.length - 1];
    const envFeats = fc ? Object.entries(fc.horizons).map(([h, v]) => ({
      type: "Feature", properties: { horizon: h }, geometry: v.envelope,
    })) : [];
    map.getSource("pilot-obs").setData({ type: "FeatureCollection", features: obsFeats });
    map.getSource("pilot-envelope").setData({ type: "FeatureCollection", features: envFeats });
    const pts = [];
    [...obsFeats, ...envFeats].forEach((f) => {
      const rings = f.geometry.type === "MultiPolygon" ? f.geometry.coordinates.flat(2) : f.geometry.coordinates.flat();
      rings.forEach((p) => Array.isArray(p) && typeof p[0] === "number" && pts.push(p));
    });
    if (pts.length) {
      let minx = 999, miny = 999, maxx = -999, maxy = -999;
      pts.forEach(([x, y]) => { minx = Math.min(minx, x); miny = Math.min(miny, y); maxx = Math.max(maxx, x); maxy = Math.max(maxy, y); });
      map.fitBounds([[minx, miny], [maxx, maxy]], { padding: 120, maxZoom: 8.5, duration: 900 });
    }
  }, []);

  const clearIncidentOnMap = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getSource("pilot-obs")?.setData({ type: "FeatureCollection", features: [] });
    map.getSource("pilot-envelope")?.setData({ type: "FeatureCollection", features: [] });
  }, []);

  const openAlert = useCallback((a) => {
    if (mapRef.current && a.lon != null) {
      mapRef.current.flyTo({ center: [a.lon, a.lat], zoom: Math.max(mapRef.current.getZoom(), 8), duration: 900 });
    }
    selectSlick(String(a.cerulean_id), a.image_ts);
  }, [selectSlick]);

  // ---- Undersea cables ----
  cablesRef.current = cablesData;
  vesselsRef.current = vessels;

  const nearbyToCable = useCallback((geometry, list) => {
    return (list || [])
      .map((v) => ({ ...v, distance_km: Math.round(distancePointToLineKm(v.lon, v.lat, geometry) * 10) / 10 }))
      .filter((v) => v.distance_km <= 10)
      .sort((a, b) => a.distance_km - b.distance_km);
  }, []);

  const selectCable = useCallback((shortName) => {
    const map = mapRef.current;
    const feat = (cablesRef.current?.features || []).find((f) => f.properties.short_name === shortName);
    if (!feat || !map) return;
    setSelected(null);
    setSelectedInfra(null);
    setSelectedSlick(null);
    setSelectedCable(feat);
    if (selectedCableIdRef.current && selectedCableIdRef.current !== shortName) {
      map.setFeatureState({ source: "cables", id: selectedCableIdRef.current }, { selected: false });
    }
    map.setFeatureState({ source: "cables", id: shortName }, { selected: true });
    selectedCableIdRef.current = shortName;
    const near = nearbyToCable(feat.geometry, vesselsRef.current);
    setCableVessels(near);
    map.getSource("cable-vessels")?.setData({
      type: "FeatureCollection",
      features: near.map((v) => ({ type: "Feature", geometry: { type: "Point", coordinates: [v.lon, v.lat] }, properties: { mmsi: v.mmsi } })),
    });
    const flat = feat.geometry.type === "MultiLineString" ? feat.geometry.coordinates.flat() : feat.geometry.coordinates;
    let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
    flat.forEach(([x, y]) => { minx = Math.min(minx, x); miny = Math.min(miny, y); maxx = Math.max(maxx, x); maxy = Math.max(maxy, y); });
    map.fitBounds([[minx, miny], [maxx, maxy]], { padding: 90, maxZoom: 6.5, duration: 900 });
  }, [nearbyToCable]);
  selectCableRef.current = selectCable;

  const closeCable = useCallback(() => {
    const map = mapRef.current;
    setSelectedCable(null);
    setCableVessels([]);
    if (map) {
      if (selectedCableIdRef.current) {
        map.setFeatureState({ source: "cables", id: selectedCableIdRef.current }, { selected: false });
        selectedCableIdRef.current = null;
      }
      map.getSource("cable-vessels")?.setData({ type: "FeatureCollection", features: [] });
    }
  }, []);

  // Load the local cable GeoJSON once (static asset, no live API dependency).
  useEffect(() => {
    fetch("/data/infrastructure/major_subsea_cables.geojson")
      .then((r) => r.json())
      .then((d) => setCablesData(d))
      .catch(() => {});
  }, []);

  // Push cable data into the map source when available.
  useEffect(() => {
    const map = mapRef.current;
    if (map && cablesData && map.getSource("cables")) map.getSource("cables").setData(cablesData);
  }, [cablesData]);

  // Recompute nearby vessels when live positions update for a selected cable.
  useEffect(() => {
    const map = mapRef.current;
    if (!selectedCable || !map) return;
    const near = nearbyToCable(selectedCable.geometry, vessels);
    setCableVessels(near);
    map.getSource("cable-vessels")?.setData({
      type: "FeatureCollection",
      features: near.map((v) => ({ type: "Feature", geometry: { type: "Point", coordinates: [v.lon, v.lat] }, properties: { mmsi: v.mmsi } })),
    });
  }, [vessels, selectedCable, nearbyToCable]);

  // Cable layer visibility toggle.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady.current) return;
    const vis = layers.cables ? "visible" : "none";
    ["cables-line", "cable-vessels-ring"].forEach((id) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
    });
    if (!layers.cables) closeCable();
  }, [layers.cables, closeCable]);


  // Slick alerts: fresh, high-confidence detections near a rig or a tracked vessel.
  useEffect(() => {
    const AOI = "95,1,122.5,25.5";
    const load = async () => {
      try {
        const d = await api.slickAlerts({ bbox: AOI, hours: 48, min_conf: 0.85 });
        setAlerts(d.alerts || []);
      } catch {
        /* ignore */
      }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  const loadSlicks = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !map.getSource("slicks")) return;
    const empty = { type: "FeatureCollection", features: [] };
    if (!layers.slicks) {
      map.getSource("slicks").setData(empty);
      return;
    }
    try {
      const b = map.getBounds();
      const bbox = `${b.getWest().toFixed(3)},${b.getSouth().toFixed(3)},${b.getEast().toFixed(3)},${b.getNorth().toFixed(3)}`;
      const { mode: m, t: tv } = slickStateRef.current;
      const end = m === "playback" ? new Date(tv) : new Date();
      const from = new Date(end.getTime() - 30 * 24 * 3600 * 1000).toISOString();
      const fc = await api.slicks({ bbox, from, to: end.toISOString() });
      if (map.getSource("slicks")) map.getSource("slicks").setData(fc);
    } catch {
      /* transient — Cerulean unavailable, keep existing polygons */
    }
  }, [layers.slicks]);

  loadSlicksRef.current = loadSlicks;
  slickStateRef.current = { mode, t };

  // Reload slicks when toggled on/off, and refresh live slicks periodically.
  useEffect(() => {
    loadSlicks();
    if (!layers.slicks || mode !== "live") return;
    const iv = setInterval(loadSlicks, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [layers.slicks, mode, loadSlicks]);

  // In playback, refetch slicks when the selected hour changes.
  const slickHour = mode === "playback" ? Math.floor(t / 3600000) : 0;
  useEffect(() => {
    if (mode === "playback") loadSlicks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slickHour]);

  const loadInfra = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !map.getSource("infra")) return;
    try {
      const fc = await api.infrastructure();
      map.getSource("infra").setData(fc);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady.current) return;
    const vis = layers.infra ? "visible" : "none";
    ["infra-glow", "infra-dot"].forEach((id) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
    });
  }, [layers.infra]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady.current) return;
    const vis = layers.slicks ? "visible" : "none";
    ["slicks-fill", "slicks-outline"].forEach((id) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
    });
  }, [layers.slicks]);

  const loadTrack = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !map.getSource("track")) return;
    if (!selected || !showTrack) {
      map.getSource("track").setData({ type: "FeatureCollection", features: [] });
      setTrackInfo(null);
      return;
    }
    const to = new Date().toISOString();
    const from = new Date(Date.now() - trackWindow.hours * 3600_000).toISOString();
    try {
      const d = await api.track(selected, from, to);
      map.getSource("track").setData({ type: "FeatureCollection", features: [d.line] });
      setTrackInfo(d.count > 0 ? { count: d.count, windowKey: trackWindow.key, from, to } : null);
    } catch {
      /* ignore */
    }
  }, [selected, showTrack, trackWindow]);

  useEffect(() => {
    loadTrack();
  }, [loadTrack]);

  const closePanel = () => {
    setSelected(null);
    setShowTrack(false);
    const map = mapRef.current;
    if (map && map.getSource("track"))
      map.getSource("track").setData({ type: "FeatureCollection", features: [] });
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[#0B0F19]">
      <div ref={containerRef} className="absolute inset-0" data-testid="map-canvas" />

      <TopBar health={health} vesselCount={filtered.length} alerts={alerts} onAlertClick={openAlert} />

      <LeftPanel
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        filters={filters}
        setFilters={setFilters}
        layers={layers}
        setLayers={setLayers}
        vessels={filtered}
        counts={counts}
        onSelect={selectVessel}
        onOpenPilot={() => setShowPilot(true)}
      />

      {selected && (
        <VesselPanel
          mmsi={selected}
          onClose={closePanel}
          showTrack={showTrack}
          setShowTrack={setShowTrack}
          trackWindow={trackWindow}
          setTrackWindow={setTrackWindow}
        />
      )}

      {selectedInfra && (
        <InfraPanel gfwId={selectedInfra} onClose={() => setSelectedInfra(null)} />
      )}

      {selectedSlick && (
        <SlickPanel slickId={selectedSlick} onClose={() => setSelectedSlick(null)} />
      )}

      {selectedCable && (
        <CablePanel
          cable={selectedCable.properties}
          vessels={cableVessels}
          radiusKm={10}
          onClose={closeCable}
          onSelectVessel={selectVessel}
        />
      )}

      {cableHover && (
        <div
          data-testid="cable-hover-tooltip"
          className="pointer-events-none absolute z-40 -translate-x-1/2 -translate-y-full"
          style={{ left: cableHover.x, top: cableHover.y - 8 }}
        >
          <span className="rounded bg-slate-900/95 border border-[#6366F1]/40 px-2 py-0.5 text-[11px] font-mono text-[#A5B4FC] whitespace-nowrap shadow-lg">
            {cableHover.short} — {cableHover.name}
          </span>
        </div>
      )}

      <PlaybackBar
        range={range}
        t={t}
        mode={mode}
        playing={playing}
        speed={speed}
        onScrub={scrub}
        onTogglePlay={togglePlay}
        onSpeed={(s) => setSpeed(s)}
        onLive={jumpLive}
      />

      <TrackLegend info={trackInfo} />

      {showPilot && (
        <PilotPanel
          onClose={() => { setShowPilot(false); clearIncidentOnMap(); }}
          onFocusIncident={showIncidentOnMap}
        />
      )}
    </div>
  );
}
